import { DateTime } from "luxon";
import type { IntlShape } from "react-intl";

export interface FormatRelativeTimeOptions {
	readonly minValidYear?: number;
	readonly thresholds?: RelativeTimeThresholds;
	readonly includeSeconds?: boolean;
	readonly includeWeeks?: boolean;
	readonly includeYears?: boolean;
	readonly monthCalculation?: "calendar" | "duration";
}

export interface RelativeTimeThresholds {
	readonly nowSeconds?: number;
	readonly minutes?: number;
	readonly hours?: number;
	readonly days?: number;
	readonly weeks?: number;
	readonly months?: number;
}

export interface DetailedTimestampOptions {
	readonly refreshIntervalMinutes?: number | null;
	readonly minValidYear?: number;
}

export const parseToDateTime = (
	timestamp: string | Date | DateTime | null | undefined,
	minValidYear: number = 1970,
): DateTime | null => {
	if (!timestamp) {
		return null;
	}

	let dt: DateTime;

	if (DateTime.isDateTime(timestamp)) {
		dt = timestamp;
	} else if (typeof timestamp === "string") {
		dt = DateTime.fromISO(timestamp);
	} else {
		dt = DateTime.fromJSDate(new Date(timestamp));
	}

	if (!dt.isValid || (minValidYear !== undefined && dt.year <= minValidYear)) {
		return null;
	}

	return dt;
};

export const formatRelativeTimeText = (
	timestamp: string | Date | DateTime | null | undefined,
	intl: IntlShape,
	options: FormatRelativeTimeOptions = {},
): string | null => {
	const dt = parseToDateTime(timestamp, options.minValidYear ?? 1970);

	if (!dt) {
		return null;
	}

	const now = DateTime.now();
	const diffSeconds = Math.max(0, Math.floor(now.diff(dt, "seconds").seconds));
	const diffMinutes = Math.floor(diffSeconds / 60);
	const diffHours = Math.floor(diffMinutes / 60);
	const diffDays = Math.floor(diffHours / 24);
	const diffWeeks = Math.floor(diffDays / 7);
	const diffMonths =
		options.monthCalculation === "duration" ? Math.floor(diffDays / 30) : Math.floor(now.diff(dt, "months").months);
	const diffYears = Math.floor(diffDays / 365);
	const thresholds = options.thresholds ?? {};

	if (options.includeSeconds === true && diffSeconds < 60) {
		return intl.formatMessage(
			{
				id: "date-time.last-updated.seconds-ago",
				defaultMessage: "{count, plural, one {# second ago} other {# seconds ago}}",
			},
			{ count: Math.max(1, diffSeconds) },
		);
	}

	if (diffSeconds < (thresholds.nowSeconds ?? 60)) {
		return intl.formatMessage({ id: "date-time.last-updated.now", defaultMessage: "now" });
	}

	if (diffMinutes < (thresholds.minutes ?? 60)) {
		return intl.formatMessage(
			{
				id: "date-time.last-updated.minutes-ago",
				defaultMessage: "{count, plural, one {# minute ago} other {# minutes ago}}",
			},
			{ count: diffMinutes },
		);
	}

	if (diffHours < (thresholds.hours ?? 24)) {
		return intl.formatMessage(
			{
				id: "date-time.last-updated.hours-ago",
				defaultMessage: "{count, plural, one {# hour ago} other {# hours ago}}",
			},
			{ count: diffHours },
		);
	}

	if (diffDays < (thresholds.days ?? 30)) {
		return intl.formatMessage(
			{
				id: "date-time.last-updated.days-ago",
				defaultMessage: "{count, plural, one {# day ago} other {# days ago}}",
			},
			{ count: diffDays },
		);
	}

	if (options.includeWeeks === true && diffWeeks < (thresholds.weeks ?? 4)) {
		return intl.formatMessage(
			{
				id: "date-time.last-updated.weeks-ago",
				defaultMessage: "{count, plural, one {# week ago} other {# weeks ago}}",
			},
			{ count: diffWeeks },
		);
	}

	if (diffMonths < (thresholds.months ?? 12)) {
		return intl.formatMessage(
			{
				id: "date-time.last-updated.months-ago",
				defaultMessage: "{count, plural, one {# month ago} other {# months ago}}",
			},
			{ count: diffMonths },
		);
	}

	if (options.includeYears === true) {
		return intl.formatMessage(
			{
				id: "date-time.last-updated.years-ago",
				defaultMessage: "{count, plural, one {# year ago} other {# years ago}}",
			},
			{ count: diffYears },
		);
	}

	return intl.formatDate(dt.toJSDate(), {
		year: "2-digit",
		month: "2-digit",
		day: "2-digit",
	});
};

export const formatLastUpdatedText = (
	timestamp: string | Date | DateTime | null | undefined,
	intl: IntlShape,
	options: FormatRelativeTimeOptions = {},
): string | null => {
	const humanReadable = formatRelativeTimeText(timestamp, intl, options);

	if (humanReadable === null) {
		return null;
	}

	return intl.formatMessage(
		{
			id: "date-time.last-updated.pattern",
			defaultMessage: "Last update | {humanReadable}",
		},
		{ humanReadable },
	);
};

export const getDetailedTimestampText = (
	timestamp: string | Date | DateTime | null | undefined,
	intl: IntlShape,
	options: DetailedTimestampOptions = {},
): string => {
	const dt = parseToDateTime(timestamp, options.minValidYear ?? 1970);

	if (!dt) {
		return intl.formatMessage({
			id: "date-time.last-updated.no-timestamp-available",
			defaultMessage: "No timestamp available",
		});
	}

	const refreshInterval = options.refreshIntervalMinutes;

	if (refreshInterval !== undefined && refreshInterval !== null && refreshInterval > 0) {
		const now = DateTime.now();
		const nextRefreshTime = dt.plus({ minutes: refreshInterval });
		const minutesUntilRefresh = nextRefreshTime.diff(now, "minutes");
		const minutesCeiling = Math.max(0, Math.ceil(minutesUntilRefresh.minutes));

		if (minutesCeiling === 0) {
			return intl.formatMessage({
				id: "date-time.last-updated.data-refreshing",
				defaultMessage: "Data is refreshing...",
			});
		}

		return intl.formatMessage(
			{
				id: "date-time.last-updated.data-will-refresh-in",
				defaultMessage: "Data will refresh in {count, plural, one {# minute} other {# minutes}}",
			},
			{ count: minutesCeiling },
		);
	}

	return intl.formatDate(dt.toJSDate(), {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
		second: "2-digit",
	});
};
